import {
  FaCode,
  FaMobileAlt,
  FaChartLine,
  FaShieldAlt,
  FaCogs,
  FaBullhorn,
} from "react-icons/fa";

export const disciplines = [
  {
    id: "full-stack-ai",
    name: "Full-Stack & AI Engineering",
    tagline: "Build intelligent web applications with modern cloud architecture",
    icon: FaCode,
    description:
      "Master end-to-end web development with AI integration and cloud-native thinking.",
    industryContext:
      "The future of web development is AI-first. Companies are hiring engineers who can build full-stack applications enhanced with AI capabilities and deployed on cloud infrastructure.",
    skills: [
      "Frontend development (React, TypeScript)",
      "Backend development (Node.js, Python)",
      "Database design and optimization",
      "AI/ML model integration",
      "Cloud deployment (AWS/GCP/Azure)",
      "API design and microservices",
      "Real-time systems",
      "DevOps basics",
    ],
    tools: [
      "React/Next.js",
      "Node.js/Express",
      "Python (FastAPI/Django)",
      "PostgreSQL/MongoDB",
      "Docker & Kubernetes basics",
      "AWS/Google Cloud",
      "Git & GitHub",
      "Postman/API testing tools",
      "OpenAI APIs",
      "LangChain",
    ],
    projects: [
      "AI-powered customer support chatbot",
      "Real-time collaborative document editor",
      "E-commerce platform with ML recommendations",
      "Analytics dashboard with live data",
    ],
    careerRoles: [
      "Full-Stack Engineer",
      "Senior Frontend Engineer",
      "Backend Engineer",
      "AI/ML Engineer",
      "Solutions Architect",
      "Tech Lead",
    ],
  },

  {
    id: "mobile-dev",
    name: "Mobile Development",
    tagline:
      "Build native and cross-platform mobile and handheld applications",
    icon: FaMobileAlt,
    description:
      "Create high-performance mobile apps for iOS, Android, and emerging platforms like TV and wearables.",
    industryContext:
      "Mobile-first is non-negotiable. Engineers who can build efficient, user-friendly apps across multiple platforms are in high demand.",
    skills: [
      "React Native/Flutter",
      "Native iOS (Swift)",
      "Native Android (Kotlin)",
      "Mobile UX/UI principles",
      "API integration",
      "Local storage and databases",
      "Performance optimization",
      "Mobile testing",
      "Push notifications",
      "Analytics integration",
    ],
    tools: [
      "React Native",
      "Flutter",
      "Swift (Xcode)",
      "Kotlin (Android Studio)",
      "Firebase",
      "Redux/Provider",
      "Git",
      "Figma",
      "TestFlight/Google Play",
      "App analytics tools",
    ],
    projects: [
      "Social networking mobile app",
      "Fitness tracking application",
      "E-commerce mobile app with payments",
      "Smart TV content app",
    ],
    careerRoles: [
      "Mobile Engineer (iOS)",
      "Mobile Engineer (Android)",
      "React Native Developer",
      "Flutter Developer",
      "Mobile Team Lead",
      "Product Engineer",
    ],
  },

  {
    id: "data-science",
    name: "Data Science & Analytics",
    tagline:
      "Turn data into actionable insights and predictive intelligence",
    icon: FaChartLine,
    description:
      "Learn data analysis, machine learning, and business intelligence to solve real-world problems.",
    industryContext:
      "Every company now needs data specialists. Data literacy is becoming a business imperative, and good data engineers can transform businesses.",
    skills: [
      "Python programming (pandas, numpy, scikit-learn)",
      "Statistics and probability",
      "Data visualization",
      "SQL and databases",
      "Machine learning algorithms",
      "Deep learning basics",
      "Data engineering concepts",
      "Business analytics",
      "A/B testing",
      "Data storytelling",
    ],
    tools: [
      "Python",
      "Pandas & NumPy",
      "Scikit-learn",
      "TensorFlow/PyTorch",
      "SQL",
      "Tableau/Power BI",
      "Jupyter Notebooks",
      "Git",
      "Apache Spark basics",
      "AWS/GCP data services",
    ],
    projects: [
      "Customer churn prediction",
      "Recommendation engine",
      "Fraud detection system",
      "Business intelligence dashboard",
      "Natural language processing model",
    ],
    careerRoles: [
      "Data Scientist",
      "Machine Learning Engineer",
      "Data Engineer",
      "Analytics Engineer",
      "Business Intelligence Developer",
      "AI/ML Architect",
    ],
  },

  {
    id: "cybersecurity",
    name: "Cybersecurity",
    tagline: "Protect systems and data from evolving cyber threats",
    icon: FaShieldAlt,
    description:
      "Master security fundamentals, ethical hacking, and secure application development.",
    industryContext:
      "Security breaches are costly and frequent. Companies desperately need security-aware engineers who can build defensively and identify vulnerabilities.",
    skills: [
      "Network security",
      "Cryptography basics",
      "Ethical hacking and penetration testing",
      "Secure coding practices",
      "Authentication and authorization",
      "Vulnerability assessment",
      "Security compliance",
      "Incident response",
      "Security architecture",
      "Cloud security",
    ],
    tools: [
      "Wireshark",
      "Metasploit",
      "Burp Suite",
      "OWASP tools",
      "Kali Linux",
      "Python (security scripting)",
      "Nmap",
      "SSL/TLS tools",
      "Git",
      "AWS/GCP security services",
    ],
    projects: [
      "Web application penetration testing",
      "Network security audit",
      "Secure API development",
      "Malware analysis",
      "Security policy implementation",
    ],
    careerRoles: [
      "Security Engineer",
      "Penetration Tester",
      "Security Analyst",
      "Application Security Engineer",
      "Cloud Security Specialist",
      "Security Architect",
    ],
  },

  {
    id: "devops-sre",
    name: "DevOps / SRE",
    tagline: "Build reliable, scalable, and automated infrastructure",
    icon: FaCogs,
    description:
      "Master infrastructure automation, deployment pipelines, and system reliability engineering.",
    industryContext:
      "DevOps and SRE are critical for modern software delivery. Organizations need engineers who can automate, scale, and maintain reliable systems.",
    skills: [
      "Linux administration",
      "Docker and containerization",
      "Kubernetes orchestration",
      "CI/CD pipelines",
      "Infrastructure as Code",
      "Cloud platforms (AWS/GCP/Azure)",
      "Monitoring and logging",
      "Disaster recovery",
      "Performance optimization",
      "Security compliance",
    ],
    tools: [
      "Docker",
      "Kubernetes",
      "Jenkins/GitLab CI",
      "Terraform",
      "Ansible",
      "Prometheus/Grafana",
      "ELK Stack",
      "AWS/Google Cloud",
      "Bash/Python scripting",
      "Git",
    ],
    projects: [
      "Containerized microservices deployment",
      "CI/CD pipeline setup",
      "Infrastructure as code implementation",
      "Kubernetes cluster management",
      "Monitoring and alerting system",
    ],
    careerRoles: [
      "DevOps Engineer",
      "Site Reliability Engineer (SRE)",
      "Infrastructure Engineer",
      "Cloud Engineer",
      "Platform Engineer",
      "Engineering Manager",
    ],
  },

  {
    id: "digital-marketing",
    name: "Advanced Digital Marketing",
    tagline:
      "Master data-driven marketing and digital growth strategies",
    icon: FaBullhorn,
    description:
      "Learn digital marketing, analytics, growth hacking, and campaign optimization.",
    industryContext:
      "Digital marketing is evolving into a data science discipline. Marketers who understand analytics, SEO, paid ads, and marketing automation drive business growth.",
    skills: [
      "SEO and content strategy",
      "Google Analytics and data analysis",
      "Paid advertising (Google Ads, Facebook Ads)",
      "Email marketing automation",
      "Social media strategy",
      "Growth hacking techniques",
      "Conversion rate optimization",
      "Marketing funnel design",
      "A/B testing and experimentation",
      "Marketing technology stack",
    ],
    tools: [
      "Google Analytics",
      "Google Ads",
      "Facebook Ads Manager",
      "SEMrush/Ahrefs",
      "Mailchimp/HubSpot",
      "Canva",
      "Buffer/Hootsuite",
      "Hotjar",
      "Google Search Console",
      "Excel/Sheets (data analysis)",
    ],
    projects: [
      "Complete digital marketing campaign",
      "SEO-optimized website launch",
      "Growth hacking experiment",
      "Email marketing funnel",
      "Social media strategy and execution",
    ],
    careerRoles: [
      "Digital Marketing Specialist",
      "Growth Marketing Manager",
      "SEO Specialist",
      "Performance Marketing Manager",
      "Marketing Analyst",
      "Marketing Director",
    ],
  },
];
